import { useMemo } from 'react';
import { getConversationStore } from '@/stores/conversation/conversationStore';

/**
 * Custom React hook to get a conversation store instance
 * 
 * This hook ensures that the same store instance is used across re-renders
 * by memoizing the store based on chatbotId and interfaceType.
 * 
 * @param {string} chatbotId - The chatbot ID
 * @param {string} interfaceType - The interface type ('in-app', 'bubble', 'direct-link', 'human-handover')
 * @returns {Object} Store actions and state
 */
export const useConversationStore = (chatbotId, interfaceType = 'in-app') => {
  // Memoize the store instance to prevent unnecessary re-creations
  const store = useMemo(() => {
    return getConversationStore(chatbotId || 'default', interfaceType);
  }, [chatbotId, interfaceType]);

  // Get the current state and actions from the store
  const currentConversation = store((state) => state.currentConversation);
  const setCurrentConversation = store((state) => state.setCurrentConversation);
  const resetCurrentConversation = store((state) => state.resetCurrentConversation);
  const reset = store((state) => state.reset);
  const setTakeoverId = store((state) => state.setTakeoverId);
  const takeoverId = store((state) => state.takeoverId);
  const addConversationToChatbot = store((state) => state.addConversationToChatbot);
  const getConversationsForChatbot = store((state) => state.getConversationsForChatbot);
  const clearConversationsForChatbot = store((state) => state.clearConversationsForChatbot);

  return {
    currentConversation,
    setCurrentConversation,
    resetCurrentConversation,
    reset,
    setTakeoverId,
    takeoverId,
    addConversationToChatbot,
    getConversationsForChatbot,
    clearConversationsForChatbot,
  };
};

